import { BannerType } from '@/app/entities/Banner'
import { useBanners } from '@/app/hooks/useBanners'
import { useOS } from '@/app/hooks/useOS'
import { usersService } from '@/app/services/usersService'
import { CreateReferralBody } from '@/app/services/usersService/createReferral'
import { useToast } from '@/view/components/ui/use-toast'
import { zodResolver } from '@hookform/resolvers/zod'
import { useMutation } from '@tanstack/react-query'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { useParams } from 'react-router-dom'
import { z } from 'zod'

const InviteFormSchema = z.object({
  name: z.string().min(1, 'Nome é obrigatório'),
  email: z.string().min(1, 'E-mail é obrigatório').email('E-mail inválido'),
  phone: z
    .string()
    .min(10, 'Telefone é obrigatório')
    .max(15, 'Número inválido')
    .transform((v) => v.replace(/\D/g, '')),
  cpf: z
    .string()
    .min(12, 'CPF é obrigatório')
    .transform((v) => v.replace(/\D/g, '')),
})

type InviteFormData = z.infer<typeof InviteFormSchema>
export function useInviteController() {
  const form = useForm<InviteFormData>({
    resolver: zodResolver(InviteFormSchema),
  })

  const { banners, isLoading } = useBanners(BannerType.INVITE)

  const { isAndroid, isIOS } = useOS()
  const [modalOpen, setModalOpen] = useState(false)

  const { id } = useParams()
  const { toast } = useToast()

  const {
    formState: { errors },
    handleSubmit,
  } = form

  const { mutateAsync: createReferral } = useMutation({
    mutationFn: (data: CreateReferralBody) => usersService.createReferral(data),
  })

  const onSubmit = handleSubmit(async (data: InviteFormData) => {
    try {
      if (!id) throw new Error('id does not exist.')

      await createReferral({
        name: data.name,
        email: data.email,
        phone: data.phone,
        cpf: data.cpf,
        referral: id.toLowerCase(),
      })

      const registerUrl = import.meta.env.VITE_TEAMS_URL.replace(
        '/invite/',
        '/register/',
      )

      if (isIOS) {
        const appleUrl = import.meta.env.VITE_APPLE_URL
        if (appleUrl && appleUrl.trim() !== '') {
          setTimeout(() => {
            window.open(appleUrl, '_blank')
          }, 100)
        } else {
          setTimeout(() => {
            window.location.href = registerUrl
          }, 100)
        }
      } else if (isAndroid) {
        const googleUrl = import.meta.env.VITE_GOOGLE_URL
        if (googleUrl && googleUrl.trim() !== '') {
          window.open(googleUrl, '_blank')
        } else {
          setTimeout(() => {
            window.location.href = registerUrl
          }, 100)
        }
      } else {
        setModalOpen(true)
      }

      form.reset()
    } catch (e) {
      console.log(e)
      toast({
        variant: 'destructive',
        title: 'Erro ao enviar convite, tente novamente mais tarde',
      })
    }
  })

  const onOpenLink = (link: string, sameTab = false) => {
    if (sameTab) {
      window.location.href = link
    } else {
      window.open(link)
    }
    setModalOpen(false)
  }

  return {
    onSubmit,
    errors,
    form,
    modalState: { modalOpen, setModalOpen },
    onOpenLink,
    banners,
    isLoading,
  }
}
